/**
 * ExaminerWorkspace (P358) -- the full SPA surface an examiner sees.
 *
 * Rendered INSTEAD of the operator shell when /v1/me carries the
 * `examiner` realm role: zero operator chrome (no nav, no queues, no
 * bell), just the curated examination surface over /v1/examiner/*.
 * The backend boundary middleware confines the token to those routes
 * regardless; this component is the honest UI for that confinement.
 */
import { listOf } from "shared/i18n/listOf";
import { PreferencesUnreachable } from "shared/api/preferencesReach";
import { Link, useLocation } from "react-router-dom";
import type { TFunction } from "i18next";
import { modelsDifferSentence } from "shared/engine/modelSentence";
import { useOptionalToast } from "shared/toast/ToastProvider";
import { LanguageToggle } from "shared/i18n/LanguageToggle";
import { Figure } from "shared/components/Figure";
import { groupCoherence } from "shared/examiner/coherenceGroups";
import {
  EXAMINER_GROUP_PACK_PATH,
  GroupRegisterDownload,
} from "shared/groups/GroupRegisterDownload";
import { checkDetail, checkLabel } from "shared/supervisory/checkWords";
import type { Said } from "shared/supervisory/checkWords";
import type { CoherenceGroup } from "shared/examiner/coherenceGroups";
import { sealedFieldLabel, sealedFieldValue } from "shared/examiner/sealedField";
import { Explainer, InfoTip } from "shared/components/Explainer";
import { useEffect, useRef, useState } from "react";
import { formatDateTime } from "shared/time/formatDate";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { recordFieldLabel } from "shared/examiner/chainFieldLabel";
import { termLabel } from "shared/vocab/governance";
import { statusLabel } from "shared/vocab/workflowState";
import { DocumentReadingNote } from "shared/documents/DocumentReadingNote";

import { api, ApiError, currentAuthToken } from "shared/api/client";
import { refusalMessage } from "shared/api/refusalMessage";
import { useAuth } from "shared/auth/AuthProvider";

interface ExaminerOverview {
  generated_at: string;
  grant: {
    examiner_username: string;
    scope_note: string;
    expires_at: string;
  } | null;
  score: {
    score: number | null;
    /** Round 19 B29 -- null when not scored: no tier beside no score. */
    tier: string | null;
    confidence: number;
    methodology_version: string;
    not_scored_reason?: string | null;
  };
  baseline: { baseline_pct: number; verdict: string };
  checks_summary: {
    checks_total: number;
    checks_flagged: number;
    /** Round 16 B9 -- checks with nothing to judge; out of the "of". */
    checks_not_assessed?: number;
    /** Round 18 P11 -- of the flagged, the coherence flags. */
    coherence_flags?: number;
    coverage_rows: number;
    /** Round 19 P18 -- the distribution checks alone, as the field exam counts them. */
    distribution_total?: number;
    distribution_flagged?: number;
  };
  /* Round 13 F8 -- where this deployment runs, how it is backed up,
     and whether the restore has ever been exercised. The panel that
     says it is administrator-only and this token is confined to these
     routes, where "hosted", "residency", "restore" and "backup"
     occurred zero times. Null means NOT STATED, which is a real
     answer and the default one. */
  deployment?: {
    region: string | null;
    hosting: string | null;
    backup: string | null;
    restore_tested: string | null;
    document_url: string | null;
    restore_recorded_at?: string | null;
    restore_disagrees?: boolean;
  } | null;
}

interface ExaminerChecks {
  generated_at: string;
  checks_total: number;
  checks_flagged: number;
  coverage_pct: number | null;
  distribution: {
    key: string;
    label: string;
    detail: string;
    ok: boolean;
    assessed?: boolean;
    /** E5 -- what the check counted; absent for compound checks. */
    spread?: { value: string; count: number }[];
    offenders?: string[];
    classes?: DistributionClass[];
  }[];
  coherence: {
    key: string;
    label: string;
    entity_kind?: string;
    entity_id?: string;
    entity_name: string;
    detail: string;
    /** D3 -- every row the flag counted, where it collapsed its own
     *  set server-side rather than emitting one flag per offender. */
    members?: { kind: string; id: string; name: string }[];
    truncated?: boolean;
  }[];
}

/** CX6 -- one class of a distribution, and the records in it. */
interface DistributionClass {
  value: string;
  count: number;
  truncated: boolean;
  members: { kind: string; id: string; name: string }[];
  /** Round 9 E4 -- how many rows there ACTUALLY are. */
  member_total?: number;
  /** F4 -- whether the cap sentence has anywhere to send him. A class
   *  grouped on a derived value has no column to re-read. */
  expandable?: boolean;
}

interface ExaminerChainVerify {
  generated_at: string;
  chains: { slug: string; examined: number; valid: number; breaks: number }[];
  total_examined: number;
  total_breaks: number;
}

interface ClassRestOut {
  check_key: string;
  class_value: string;
  total: number;
  limit: number;
  truncated: boolean;
  members: { kind: string; id: string; name: string }[];
}

/**
 * Round 11 F4 -- the rest of a capped class, on request.
 *
 * Fetched only when asked, for the reason the chain rows are: a
 * supervisor reading a summary should not pull two hundred records per
 * class of every check on the page. The wider cap is still a cap and
 * is still stated, because a list that silently ends is the defect
 * this replaces.
 */
function ClassRest({
  checkKey,
  classValue,
  shown,
  count,
  onOpenEntity,
}: {
  checkKey: string;
  classValue: string;
  shown: number;
  count: number;
  onOpenEntity: (kind: string, id: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rest = useExaminer<ClassRestOut>(
    `supervisory-checks/${encodeURIComponent(checkKey)}/classes/${encodeURIComponent(classValue)}`,
    open,
  );
  if (!open) {
    return (
      <li>
        <button
          type="button"
          data-testid={`examiner-class-more-${checkKey}-${classValue}`}
          onClick={() => setOpen(true)}
          className="text-brand-brassDark hover:underline"
        >
          {t("examiner.check.classMore", { shown, count })}
        </button>
      </li>
    );
  }
  if (rest.isLoading) {
    return <li className="text-slate-400">{t("common.loading")}</li>;
  }
  if (rest.isError || !rest.data) {
    // The summary line it replaced, rather than a blank: the count is
    // still true and is still the thing the reader was told.
    return (
      <li
        data-testid={`examiner-class-more-failed-${checkKey}`}
        className="text-slate-400"
      >
        {t("examiner.check.classMore", { shown, count })}
      </li>
    );
  }
  return (
    <>
      {rest.data.members.slice(shown).map((m) => (
        <li key={m.id}>
          <button
            type="button"
            data-testid={`examiner-class-member-${m.id}`}
            onClick={() => onOpenEntity(m.kind, m.id, m.name)}
            className="text-brand-brassDark hover:underline"
            dir="auto"
          >
            {m.name}
          </button>
        </li>
      ))}
      {rest.data.truncated && (
        <li
          data-testid={`examiner-class-still-capped-${checkKey}`}
          className="text-slate-400"
        >
          {t("examiner.check.classMore", {
            shown: rest.data.members.length,
            count: rest.data.total,
          })}
        </li>
      )}
    </>
  );
}

function useExaminer<T>(path: string, enabled = true) {
  return useQuery<T, ApiError>({
    queryKey: ["examiner", path],
    queryFn: () => api.get<T>(`/v1/examiner/${path}`),
    retry: false,
    // Chain entries are fetched only when a row is opened -- an
    // examiner reading the summary should not pull sixteen chains.
    enabled,
  });
}

/** Round 18 I18 -- a refused download, as the coded refusal it is, so
 *  the screen says it from the catalogue rather than the server's
 *  English. */
async function refusedDownload(response: Response): Promise<ApiError> {
  let body: { detail?: unknown; code?: unknown; params?: unknown } = {};
  try {
    body = JSON.parse(await response.text()) as typeof body;
  } catch {
    body = {};
  }
  return new ApiError(
    response.status,
    typeof body.detail === "string" && body.detail
      ? body.detail
      : `HTTP ${response.status}`,
    undefined,
    typeof body.code === "string" ? body.code : undefined,
    body.params && typeof body.params === "object"
      ? (body.params as Record<string, string>)
      : undefined,
  );
}

async function downloadDossier(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch("/v1/examiner/dossier.zip", {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    // The server's own sentence says whether the grant was revoked,
    // expired, or something else -- an examiner locked out mid-visit
    // needs to know which, and whom to ask.
    throw await refusedDownload(response);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-examiner-dossier.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

async function downloadRfiPack(rfiId: string): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch(`/v1/examiner/rfis/${rfiId}/pack.zip`, {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw await refusedDownload(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `marsad-rfi-${rfiId}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** Round 13 F8 -- one deployment fact, or the words "not stated".
 *
 * An empty cell reads as an oversight; "not stated" is an answer, and
 * it is the one the product must give rather than inferring a region
 * from anything. Residency is a claim a bank makes to its regulator.
 */
function DeploymentFact({
  testid,
  label,
  value,
}: {
  testid: string;
  label: string;
  value: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid={testid}>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={value ? "text-slate-900" : "italic text-slate-500"}>
        {value ?? t("examiner.deployment.notStated")}
      </dd>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  testid,
  termKey,
}: {
  label: string;
  value: string;
  sub?: string | undefined;
  testid: string;
  /** F2: the tile labels are constructs in their own right. */
  termKey?: string;
}) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
    >
      <p className="font-mono text-[11px] uppercase tracking-wide text-brand-brassDark">
        {label}
        {termKey && <InfoTip termKey={termKey} />}
      </p>
      {/* D2 -- a stat value is a figure or a pair of figures, and the
          pair is atomic. Isolating each number separately still lets
          the bidi algorithm swap which one the reader meets first, so
          "17 / 25" was read back as 25 flagged of 17. The whole value
          is isolated, not its parts. */}
      <p className="mt-1 font-display text-2xl text-slate-900">
        <Figure>{value}</Figure>
      </p>
      {sub && (
        <p className="mt-0.5 text-xs text-slate-500" dir="auto">
          {sub}
        </p>
      )}
    </div>
  );
}

/** The genesis row's previous hash is all zero bytes, which is a
 *  statement that nothing precedes it -- not a hash to print. */
function isGenesis(prevHash: string): boolean {
  return /^0+$/.test(prevHash);
}

interface SealedEntry {
  row_hash: string;
  prev_hash: string;
  captured_at: string;
  captured_by: string;
  entity_kind: string | null;
  entity_id: string | null;
  sealed_fields: Record<string, unknown>;
  /** E5 -- ids the server resolved to the names they stand for. The
   *  client cannot do this itself: the examiner token is confined to
   *  /v1/examiner/*, so it has no route to a users or entity lookup. */
  resolved?: Record<string, string>;
  /** Round 14 A7 -- fields the row carries that the hash does NOT cover
   *  (the configured model, beside the model that answered). The server
   *  has sent these since round 13 F4; this screen never rendered them,
   *  which is why "configured" occurred zero times on it. */
  unsealed_fields?: Record<string, unknown>;
  /** ROUND 15 A15 -- the model that answered is not the configured one. */
  models_differ?: boolean | null;
}

interface ChainEntries {
  slug: string;
  total: number;
  entries: SealedEntry[];
  verified_examined: number;
  verified_breaks: number;
}

/**
 * One chain, openable (B7).
 *
 * The table showed a name and a count and stopped there, so the one
 * thing an examiner most wants to inspect -- the ceremony that granted
 * him access -- was as inert as the rest. Opening a row shows the rows
 * the seal covers and re-walks that chain on its own.
 */
/* C2 -- a page, and the ceiling the endpoint enforces. Named rather
   than inlined, because a cap that is not stated is the failure this
   programme keeps finding. */
const CHAIN_PAGE = 25;
const CHAIN_MAX = 500;

/** ROUND 15 H8 -- the kinds a sealed row can open as a record: the
 *  ones the examiner's record card can compose. */
const OPENABLE_KINDS = new Set([
  "ibs_service",
  "service",
  "capability",
  "vendor",
  "configuration_item",
  "outsourcing_contract",
  "facility",
  "connector",
  "data_asset",
  "document",
]);

function ChainRow({
  chain,
  onOpenEntity,
}: {
  chain: { slug: string; examined: number; valid: number; breaks: number };
  onOpenEntity?: (kind: string, id: string, name: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  /* Round 12 C2 -- the panel that carries the Engine's entire record
     showed "the 25 most recent of 131 sealed entries" and offered no
     control that reached the other 106. That is the same ceiling the
     class list carried for seven rounds before round 12 turned it into
     a button, on the surface a supervisor uses to satisfy himself about
     a chain. The endpoint has admitted a limit up to 500 all along. */
  const [limit, setLimit] = useState(CHAIN_PAGE);
  const entries = useExaminer<ChainEntries>(
    `chains/${chain.slug}?limit=${limit}`,
    open,
  );
  const entityLabel = `${t("examiner.chain.entityLabel")}=`;
  // D11 -- the table showed a verified-at stamp, and the only way to
  // make it move was to reload the page and re-walk all seventeen. An
  // examiner who wants to satisfy himself about the chain behind ONE
  // claim had to re-verify everything to do it, which is slow enough
  // that he does not -- so the check he came for is the one he takes on
  // trust.
  const [reverified, setReverified] = useState<{
    examined: number;
    valid: number;
    breaks: number;
  } | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const shown = reverified ?? chain;

  return (
    <>
      <tr
        className="cursor-pointer border-t border-paper-2 hover:bg-slate-50"
        data-testid={`examiner-chain-row-${chain.slug}`}
        onClick={() => setOpen((v) => !v)}
      >
        <td className="py-1 font-mono text-xs">
          <span aria-hidden className="me-1 text-slate-400">
            {open ? "\u2212" : "+"}
          </span>
          {chain.slug}
        </td>
        {/* ROUND 13 H -- four of seventeen chains read 0 / 0 / 0 with
            nothing distinguishing a chain that has never been written
            from one whose every row verified. A row of zeroes is the
            same glyph for "nothing has happened here" and "nothing is
            wrong here", and on a page built for a supervisor those are
            not the same claim. */}
        <td className="text-end tabular-nums">
          {shown.examined === 0 ? (
            <span
              data-testid={`examiner-chain-empty-${chain.slug}`}
              title={t("examiner.chain.neverWrittenWhy")}
              className="text-slate-400"
            >
              {t("examiner.chain.neverWritten")}
            </span>
          ) : (
            shown.examined
          )}
        </td>
        <td className="text-end tabular-nums">
          {shown.examined === 0 ? (
            <span className="text-slate-300">--</span>
          ) : (
            shown.valid
          )}
        </td>
        {/* Round 18 B22 -- "never written" sat in green on sixteen
            chains: green is a verdict, and nothing had been walked. */}
        <td
          data-testid={`examiner-chain-breaks-${chain.slug}`}
          className={
            shown.breaks > 0
              ? "text-end font-semibold tabular-nums text-rose-700"
              : shown.examined === 0
                ? "text-end tabular-nums text-slate-400"
                : "text-end tabular-nums text-emerald-700"
          }
        >
          {shown.examined === 0 ? (
            <span className="text-slate-300">--</span>
          ) : (
            shown.breaks
          )}
        </td>
        <td className="text-end">
          <button
            type="button"
            data-testid={`examiner-chain-verify-${chain.slug}`}
            disabled={verifying}
            onClick={(ev) => {
              // The row itself opens the entries; this does not.
              ev.stopPropagation();
              setVerifying(true);
              setVerifyError(null);
              api
                .get<{ examined: number; valid: number; breaks: number }>(
                  `/v1/examiner/chain-verify/${chain.slug}`,
                )
                .then((r) => {
                  setReverified({
                    examined: r.examined,
                    valid: r.valid,
                    breaks: r.breaks,
                  });
                  setVerifiedAt(new Date().toISOString());
                })
                .catch((e: ApiError) => setVerifyError(e.detail || e.message))
                .finally(() => setVerifying(false));
            }}
            className="text-xs font-semibold text-brand-brassDark hover:underline disabled:opacity-50"
          >
            {verifying
              ? t("examiner.chain.verifying")
              : reverified
                ? t("examiner.chain.verifyAgain")
                : t("examiner.chain.verifyNow")}
          </button>
        </td>
      </tr>
      {verifyError && (
        <tr>
          <td colSpan={5} className="px-3 pb-1 text-xs text-rag-red">
            {verifyError}
          </td>
        </tr>
      )}
      {/*
        A5 -- the button re-walked the chain and changed only its own
        label, so the one act on this page that PROVES something said
        nothing about what it had proved. Same sentence the
        constellation's verify uses, because a supervisor comparing two
        surfaces should not have to decide whether two wordings mean
        the same thing.
      */}
      {reverified && !verifyError && (
        <tr>
          <td
            colSpan={5}
            data-testid={`examiner-chain-verified-${chain.slug}`}
            data-empty={reverified.examined === 0 ? "true" : "false"}
            className={
              reverified.breaks > 0
                ? "px-3 pb-1 text-xs font-semibold text-rose-700"
                : reverified.examined === 0
                  ? "px-3 pb-1 text-xs text-slate-500"
                  : "px-3 pb-1 text-xs text-emerald-800"
            }
          >
            {/* Round 19 B30 -- a chain nothing was ever written to is not
                "intact" in emerald: there was nothing to walk. */}
            {reverified.examined === 0
              ? t("examiner.chain.verifyEmpty", { slug: chain.slug })
              : t("examiner.chain.verifyResult", {
                  slug: chain.slug,
                  examined: reverified.examined,
                  valid: reverified.valid,
                  breaks: reverified.breaks,
                  at: formatDateTime(verifiedAt ?? "", i18n.language),
                })}
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={5} className="bg-slate-50 px-3 py-2">
            {entries.isLoading && (
              <p className="text-xs text-slate-500">{t("examiner.chain.loading")}</p>
            )}
            {entries.data && (
              <div data-testid={`examiner-chain-entries-${chain.slug}`}>
                <p className="mb-1 text-[11px] text-slate-500">
                  {t("examiner.chain.showing", {
                    shown: entries.data.entries.length,
                    total: entries.data.total,
                  })}{" "}
                  {t("examiner.chain.perEntity")}{" "}
                  {entries.data.entries.length < entries.data.total && (
                    <button
                      type="button"
                      data-testid={`examiner-chain-more-${chain.slug}`}
                      onClick={() =>
                        setLimit((n) => Math.min(n + CHAIN_PAGE, CHAIN_MAX))
                      }
                      disabled={limit >= CHAIN_MAX}
                      className="font-semibold text-brand-brassDark hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      {limit >= CHAIN_MAX
                        ? t("examiner.chain.showMoreCapped", { max: CHAIN_MAX })
                        : t("examiner.chain.showMore", {
                            count: Math.min(
                              CHAIN_PAGE,
                              entries.data.total - entries.data.entries.length,
                            ),
                          })}
                    </button>
                  )}
                </p>
                <ul className="space-y-1">
                  {entries.data.entries.map((e) => (
                    <li
                      key={e.row_hash}
                      className="rounded border border-slate-200 bg-white px-2 py-1"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-3 text-[11px]">
                        <span className="font-mono text-slate-500">
                          {e.captured_at.slice(0, 19).replace("T", " ")}
                        </span>
                        <span className="text-slate-700">{e.captured_by}</span>
                        {/* C5 -- the row hash was shown and the PREVIOUS
                            hash was not, on the one surface built for
                            verifying chains. The link to the row before
                            is what makes this a chain rather than a
                            list; without it an examiner is asked to
                            take the word "chain" on trust.

                            The genesis row's previous hash is 32 zero
                            bytes, which reads as a hash rather than as
                            "there is nothing before this one". */}
                        <span
                          data-testid="examiner-chain-link"
                          className="font-mono text-slate-400"
                          title={`${e.prev_hash} -> ${e.row_hash}`}
                        >
                          {/* B6 -- "genesis" three entries deep read as
                              a contradiction of the line above, which
                              says each entry links to the one before.
                              Both are true: every chain here threads
                              PER ENTITY, so each grant opens its own
                              and a revoke links back to its own mint.
                              The row says which it is rather than
                              leaving an examiner to reconcile them. */}
                          {isGenesis(e.prev_hash)
                            ? t("examiner.chain.firstForEntity", {
                                kind: e.entity_kind ?? t("examiner.chain.entityLabel"),
                              })
                            : `${e.prev_hash.slice(0, 8)}…`}
                          {" \u2192 "}
                          {e.row_hash.slice(0, 12)}…
                        </span>
                        {/* ROUND 15 H8 -- the document-ingest chain
                            sealed every upload against a document and
                            linked to none of them. A sealed row whose
                            entity has a record opens it. */}
                        {onOpenEntity &&
                          e.entity_kind &&
                          e.entity_id &&
                          OPENABLE_KINDS.has(e.entity_kind) && (
                            <button
                              type="button"
                              data-testid={`examiner-chain-open-${e.entity_id}`}
                              onClick={() =>
                                onOpenEntity(
                                  e.entity_kind!,
                                  e.entity_id!,
                                  e.resolved?.entity_id ?? e.entity_id!,
                                )
                              }
                              className="text-brand-brassDark hover:underline"
                            >
                              {t("examiner.chain.openRecord")}
                            </button>
                          )}
                      </div>
                      {/* E5 -- this printed raw UUIDs, so an examiner
                          reading his OWN grant met
                          `entity_id=fe353102-...`: the record saying
                          nothing to the person it is for. A resolved
                          name replaces the id; an id that resolved to
                          nothing keeps its raw value rather than being
                          hidden. */}
                      <div className="mt-0.5 break-all text-[10.5px] text-slate-600">
                        {Object.entries(e.sealed_fields)
                          .filter(([k]) => k !== "entity_kind")
                          .map(([k, v]) => {
                            const name = e.resolved?.[k];
                            // D11 -- this printed the canonical fields
                            // exactly as the seal holds them:
                            // `payload_hash=` and sixty-four characters,
                            // in one run with everything else. Those
                            // are the bytes the hash is computed over,
                            // which is why they are stored that way --
                            // but the surface built for a supervisor was
                            // showing him the input to a hash function
                            // and calling it a record.
                            //
                            // Composed outside the JSX: the field name
                            // is data off the seal, not screen copy, and
                            // the catalogue rule reads any literal in
                            // JSX children as copy.
                            const fieldLabel = `${sealedFieldLabel(k)}: `;
                            const rendered = sealedFieldValue(v);
                            return (
                              <span key={k} className="me-3 inline-block">
                                {/* ROUND 13 H -- a separator in the
                                    TEXT. `me-3` separates these on
                                    screen and not in innerText, so
                                    every chain read as one run of
                                    glued fields to anything copying
                                    the row into a board pack, and to
                                    a screen reader. The same defect
                                    round 11 F3 fixed one surface
                                    over. */}
                                <span aria-hidden="true">{" · "}</span>
                                <span className="text-slate-400">{fieldLabel}</span>
                                {name ? (
                                  <span className="text-slate-800">{name}</span>
                                ) : (
                                  <span
                                    className="font-mono"
                                    // Folded, never hidden: the stored
                                    // value is what the verifier walks.
                                    title={
                                      rendered.shortened ? rendered.full : undefined
                                    }
                                  >
                                    {rendered.text}
                                  </span>
                                )}
                                {!name &&
                                  rendered.shortened &&
                                  HEX_FULL.test(rendered.full) && (
                                    <>
                                      {" "}
                                      <CopyFull value={rendered.full} />
                                    </>
                                  )}
                              </span>
                            );
                          })}
                        {e.resolved?.entity_id && (
                          <span className="me-3 inline-block">
                            <span className="text-slate-400">{entityLabel}</span>
                            <span className="text-slate-800">
                              {e.resolved.entity_id}
                            </span>
                          </span>
                        )}
                      </div>
                      <UnsealedFields fields={e.unsealed_fields} />
                      {e.models_differ === true && (
                        <div
                          data-testid="examiner-chain-models-differ"
                          className="mt-0.5 text-[10.5px] text-amber-800"
                        >
                          {modelsDifferSentence(
                            t,
                            String(e.sealed_fields.model ?? ""),
                            String(e.sealed_fields.configured_model ?? ""),
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Round 14 A7 -- what the row carries that the seal does not cover, under
 * a heading that says so. Sealing the configured model instead would fold
 * a deployment's configuration into every hash, so changing the model
 * would invalidate every row ever sealed; showing it, and saying the seal
 * does not cover it, is the honest form.
 */
function UnsealedFields({
  fields,
}: {
  fields: Record<string, unknown> | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  const entries = Object.entries(fields ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return null;
  return (
    <div
      data-testid="examiner-unsealed-fields"
      className="mt-0.5 break-all text-[10.5px] text-slate-600"
    >
      <span className="italic text-slate-500">{t("examiner.chain.notSealed")}</span>
      {entries.map(([k, v]) => {
        const fieldLabel = `${sealedFieldLabel(k)}: `;
        return (
          <span key={k} className="me-3 inline-block">
            <span aria-hidden="true">{" · "}</span>
            <span className="text-slate-400">{fieldLabel}</span>
            <span className="font-mono">{sealedFieldValue(v).text}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * E5 -- one distribution check, openable.
 *
 * The rows were inert: a flagged check said "all 205 valued rows carry
 * one identical value" and there was nothing to press, so an examiner
 * could read the verdict and not the evidence for it. Opening one
 * shows what it counted -- each value and how many rows carry it.
 *
 * A check whose verdict is a compound condition rather than one column
 * has no single spread, and says so rather than opening on nothing.
 *
 * C5 -- saying so was still a dead end. "3 with NO delivery channel"
 * opened to an explanation of why there was nothing to show, and the
 * examiner's next question is always WHICH three. A compound check has
 * no distribution and can still name what it counted, so it does.
 */
function CheckRow({
  check,
  onOpenEntity,
}: {
  check: {
    key: string;
    label: string;
    detail: string;
    detail_said?: Said[];
    detail_join?: string;
    ok: boolean;
    /** Round 16 B9 -- false where there was nothing to judge. */
    assessed?: boolean;
    spread?: { value: string; count: number }[];
    offenders?: string[];
    classes?: DistributionClass[];
  };
  onOpenEntity: (kind: string, id: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const spread = check.spread ?? [];
  const offenders = check.offenders ?? [];
  const classes = check.classes ?? [];
  const checkKey = check.key;
  return (
    <li className="rounded border border-paper-2 bg-white px-3 py-2 text-sm">
      <button
        type="button"
        data-testid={`examiner-check-${check.key}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-2 text-start"
      >
        {/* Round 16 B9 -- three states, not two. A check the server
            marks not assessed (no floor list, no report with a stated
            deadline, no function on the register) carried the emerald
            OK a passed check carries. */}
        <span
          data-testid={`examiner-check-state-${check.key}`}
          data-state={
            check.assessed === false ? "not_assessed" : check.ok ? "ok" : "flagged"
          }
          className={
            check.assessed === false
              ? "rounded bg-slate-100 px-1.5 text-xs font-semibold text-slate-600"
              : check.ok
                ? "rounded bg-emerald-100 px-1.5 text-xs font-semibold text-emerald-800"
                : "rounded bg-rose-100 px-1.5 text-xs font-semibold text-rose-800"
          }
        >
          {check.assessed === false
            ? t("examiner.notAssessed")
            : check.ok
              ? t("examiner.ok")
              : t("examiner.flagged")}
        </span>
        {/* ROUND 15 I7 -- in the reader's words; the server's English
            is the fallback. */}
        <span className="font-medium">{checkLabel(t, check)}</span>
        <span className="text-xs text-slate-500">{checkDetail(t, check)}</span>
        {/* C5 -- a coherence row carries its whole statement inline and
            does not open. Without an affordance the two read alike, so
            the examiner clicked a coherence row, got nothing, and
            recorded a dead end. Only what opens looks like it does. */}
        <span aria-hidden className="ms-auto text-xs text-slate-400">
          {open ? "\u2212" : "+"}
        </span>
      </button>
      {open && (
        <div
          data-testid={`examiner-check-detail-${check.key}`}
          className="mt-2 border-t border-slate-100 pt-2 text-xs"
        >
          {/* CX6 -- the spread said a class held 205 rows and stopped.
              A supervisor's next question is always WHICH, and the only
              answer was to ask the bank. Each class now names the
              records in it, capped, with the cap stated -- a silent cut
              is the failure this programme keeps finding. */}
          {classes.length > 0 ? (
            <ul className="space-y-1.5">
              {classes.map((k) => (
                <li key={k.value}>
                  <p className="text-slate-700">
                    {/* ROUND 15 H8 -- the class was printed as the
                        column's value, `ibs_supporting`. */}
                    <span className="font-medium" title={k.value}>
                      {termLabel(k.value)}
                    </span>{" "}
                    <span className="tabular-nums text-slate-900">{k.count}</span>
                  </p>
                  <ul className="ms-3 flex flex-wrap gap-x-2 gap-y-0.5">
                    {k.members.map((m) => (
                      <li key={m.id}>
                        <button
                          type="button"
                          data-testid={`examiner-class-member-${m.id}`}
                          onClick={() => onOpenEntity(m.kind, m.id, m.name)}
                          className="text-brand-brassDark hover:underline"
                          dir="auto"
                        >
                          {m.name}
                        </button>
                      </li>
                    ))}
                    {k.truncated && k.expandable === false && (
                      /* A class grouped on a DERIVED value has no
                         column to re-read, so the count is stated and
                         no button is offered: one that 404s is the
                         dead end, not the fix. */
                      <li className="text-slate-400">
                        {t("examiner.check.classMoreFixed", {
                          shown: k.members.length,
                          count: k.count,
                        })}
                      </li>
                    )}
                    {k.truncated && k.expandable !== false && (
                      /* Round 11 F4, seventh round -- this sentence
                         has been inert since CX6 wrote it. Stating a
                         cap is better than cutting silently and it is
                         not an answer: a supervisor told a class holds
                         205 rows and shown twelve has been told where
                         to stop rather than where to look. */
                      <ClassRest
                        checkKey={checkKey}
                        classValue={k.value}
                        shown={k.members.length}
                        count={k.count}
                        onOpenEntity={onOpenEntity}
                      />
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          ) : spread.length > 0 ? (
            <table className="w-full max-w-md">
              <tbody>
                {spread.map((row) => (
                  <tr key={row.value}>
                    <td className="py-0.5 text-slate-700" title={row.value}>
                      {termLabel(row.value)}
                    </td>
                    <td className="py-0.5 text-end tabular-nums text-slate-900">
                      {row.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : offenders.length > 0 ? (
            <div data-testid={`examiner-check-offenders-${check.key}`}>
              <p className="mb-1 text-slate-500">
                {t("examiner.check.offendersHeading", {
                  count: offenders.length,
                })}
              </p>
              <ul className="list-disc space-y-0.5 ps-4">
                {offenders.map((name) => (
                  <li key={name} className="text-slate-800">
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-slate-500">{t("examiner.check.noSpread")}</p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * CX6 -- the record a check named, as a supervisor may read it.
 *
 * The links waited for somewhere to land: the boundary confines an
 * examiner to `/v1/examiner/*`, so a link into the operator's estate
 * would have refused him, and a route that refuses a supervisor is
 * worse than none.
 *
 * The seals are the point. The card tells him what the bank HOLDS; the
 * seals tell him what the bank has COMMITTED to, and each opens in the
 * chain view he already has.
 */
function EntityCard({
  kind,
  id,
  name,
  onClose,
}: {
  kind: string;
  id: string;
  name: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const entity = useExaminer<ExaminerEntity>(`entities/${kind}/${id}`);
  // H8 -- a chain row or a control's policy opens this card from well
  // below it on the page; it comes into view rather than opening where
  // nobody is looking.
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    card.current?.scrollIntoView?.({ block: "nearest" });
  }, [kind, id]);
  return (
    <div
      ref={card}
      data-testid="examiner-entity-card"
      className="rounded border border-brand-brass/40 bg-white px-3 py-2 text-sm"
    >
      <div className="flex items-baseline gap-2">
        <span className="font-medium" dir="auto">
          {entity.data?.name ?? name}
        </span>
        <span className="font-mono text-[11px] text-slate-500" dir="auto">
          {entity.data?.code}
        </span>
        <button
          type="button"
          data-testid="examiner-entity-close"
          onClick={onClose}
          className="ms-auto text-xs text-slate-500 hover:text-slate-800"
        >
          {t("examiner.entity.close")}
        </button>
      </div>
      {entity.isError && (
        <p className="mt-1 text-xs text-rag-red">{t("examiner.entity.error")}</p>
      )}
      {entity.data && (
        <>
          <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs sm:grid-cols-3">
            {Object.entries(entity.data.attributes).map(([k, v]) => (
              <div key={k}>
                <dt className="text-slate-500">{recordFieldLabel(t, k)}</dt>
                <dd className="text-slate-900" dir="auto">
                  <Figure>{v === null ? "\u2014" : String(v)}</Figure>
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-1 text-xs text-slate-600">
            {t("examiner.entity.findings", {
              count: entity.data.open_finding_count,
              severity: entity.data.worst_severity ?? "\u2014",
            })}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-slate-400">
            {t("examiner.entity.sealsHeading")}
          </p>
          {entity.data.seals.length === 0 ? (
            /* An absence is an answer. A record nothing has sealed is a
               different thing from one whose seals failed to load, and
               a supervisor is entitled to know which. */
            <p
              data-testid="examiner-entity-no-seals"
              className="text-xs text-slate-500"
            >
              {t("examiner.entity.noSeals")}
            </p>
          ) : (
            <ul data-testid="examiner-entity-seals" className="space-y-0.5 text-xs">
              {entity.data.seals.map((seal) => (
                <li key={seal.slug} className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-slate-700">{seal.slug}</span>
                  <span className="tabular-nums text-slate-900">{seal.rows}</span>
                  <span className="text-slate-500">
                    {formatDateTime(seal.latest_captured_at, i18n.language)}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400" dir="auto">
                    {seal.latest_captured_by}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

interface ExaminerEntity {
  kind: string;
  id: string;
  code: string;
  name: string;
  description: string | null;
  attributes: Record<string, unknown>;
  open_finding_count: number;
  worst_severity: string | null;
  seals: {
    slug: string;
    rows: number;
    latest_captured_at: string;
    latest_captured_by: string;
  }[];
}

interface RfiCitation {
  kind: string;
  ref: string;
  label: string;
  chain: string | null;
  detail: Record<string, unknown>;
}

interface Rfi {
  id: string;
  question: string;
  status: string;
  asked_at: string;
  answered_at: string | null;
  answer_note: string | null;
  answered_by: string | null;
  citations: RfiCitation[];
  has_pack: boolean;
  /** Round 19 S8 -- who an open question waits with. */
  recipient_names?: string[] | null;
}

/**
 * A3 -- what the bank actually pointed at.
 *
 * The answered row showed a note and a Download button, so the only
 * way to learn whether the bank had cited anything at all was to unzip
 * a signed archive. A citation is the substance of the answer; it
 * belongs in the correspondence.
 *
 * Whether the cited judgement is SEALED is rendered plainly, because
 * round 5's pack carried an unsealed grade and looked no different
 * from one an examiner could check.
 */
function CitationLine({ c }: { c: RfiCitation }) {
  const { t } = useTranslation();
  const assessment = c.detail.assessment as
    { status?: string; graded_by?: string | null } | null | undefined;
  const sealed = c.detail.sealed === true;
  return (
    <li data-testid="examiner-rfi-citation" className="text-xs text-slate-700">
      <span className="font-mono" dir="auto">
        {c.label}
      </span>
      {assessment?.status ? (
        <span className="ms-2 text-slate-500">
          {t("examiner.rfi.citedGrade", {
            status: assessment.status,
            who: assessment.graded_by ?? t("examiner.rfi.graderUnknown"),
          })}
        </span>
      ) : null}
      <span
        data-testid="examiner-rfi-citation-seal"
        className={
          sealed
            ? "ms-2 rounded bg-emerald-100 px-1.5 text-emerald-800"
            : "ms-2 rounded bg-amber-100 px-1.5 text-amber-900"
        }
      >
        {sealed ? t("examiner.rfi.sealed") : t("examiner.rfi.notSealed")}
      </span>
    </li>
  );
}

/**
 * E6 -- the examiner asks.
 *
 * The workspace showed a supervisor everything the bank asserts and
 * gave him no way to ask about any of it. An examination is a
 * correspondence: the question, when it was asked, and what came back.
 */
interface ScoreSeal {
  id: string;
  sealed_at: string;
  score: string;
  tier: string;
  confidence: string;
  methodology_version: string;
  sealed_by_kind: string;
  reason: "first" | "methodology" | "pillars" | "inputs";
  moved: { pillar: string; before: string; after: string }[];
  previous_methodology: string | null;
}

interface ScoreSeals {
  generated_at: string;
  as_of: string | null;
  in_force: ScoreSeal | null;
  seals: ScoreSeal[];
  total: number;
}

/**
 * Round 16 H30 (M5 fix 7) -- which figure to cite, why each seal was
 * sealed, and the figure as it stood on a date.
 *
 * The tile is computed from the live estate when the page is read; a
 * seal is what the bank committed, and is what the chain proves. They
 * differed (48.9 against 49.7) with nothing saying which to cite, and a
 * seal of 64.9 sat between two of 84.9 with no reason on it.
 */
export function ScoreToCite({ live, readAt }: { live: unknown; readAt: string }) {
  const { t, i18n } = useTranslation();
  const [asOf, setAsOf] = useState("");
  const iso = asOf ? new Date(asOf).toISOString() : "";
  const seals = useExaminer<ScoreSeals>(
    iso ? `score-seals?as_of=${encodeURIComponent(iso)}` : "score-seals",
  );
  // A partial payload (a sibling's catch-all `{}`) must not take the
  // seat down with it.
  const d = seals.data && Array.isArray(seals.data.seals) ? seals.data : undefined;
  const latest = d?.seals[0] ?? null;

  function why(s: ScoreSeal): string {
    if (s.reason === "first") return t("examiner.score.reason.first");
    if (s.reason === "methodology")
      return t("examiner.score.reason.methodology", {
        from: s.previous_methodology ?? "",
        to: s.methodology_version,
      });
    if (s.reason === "inputs") return t("examiner.score.reason.inputs");
    return s.moved
      .map((m) =>
        t("examiner.score.reason.moved", {
          pillar: t(`examiner.score.pillar.${m.pillar}`, { defaultValue: m.pillar }),
          before: m.before,
          after: m.after,
        }),
      )
      .join("; ");
  }
  function by(s: ScoreSeal): string {
    return t(`examiner.score.by.${s.sealed_by_kind}`, {
      defaultValue: t("examiner.score.by.other", { kind: s.sealed_by_kind }),
    });
  }

  return (
    <section className="space-y-2" data-testid="examiner-score-cite">
      <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
        {t("examiner.score.heading")}
      </h2>
      {latest ? (
        <p
          data-testid="examiner-score-cite-which"
          className="max-w-3xl text-sm text-slate-700"
        >
          {t("examiner.score.which", {
            live: String(live ?? "—"),
            read: formatDateTime(readAt, i18n.language),
            sealed: latest.score,
            sealedAt: formatDateTime(latest.sealed_at, i18n.language),
          })}
        </p>
      ) : (
        d && (
          <p className="max-w-3xl text-sm text-slate-600">
            {t("examiner.score.noSeal")}
          </p>
        )
      )}
      <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        {t("examiner.score.asOfLabel")}
        <input
          type="datetime-local"
          data-testid="examiner-score-as-of"
          aria-label={t("examiner.score.asOfLabel")}
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
      </label>
      {iso && d && (
        <p data-testid="examiner-score-in-force" className="text-sm text-slate-700">
          {d.in_force
            ? t("examiner.score.inForce", {
                when: formatDateTime(iso, i18n.language),
                score: d.in_force.score,
                tier: d.in_force.tier,
                sealedAt: formatDateTime(d.in_force.sealed_at, i18n.language),
              })
            : t("examiner.score.noneInForce", {
                when: formatDateTime(iso, i18n.language),
              })}
        </p>
      )}
      {d && d.seals.length > 0 && (
        <ul
          data-testid="examiner-score-seals"
          className="space-y-1 text-xs text-slate-700"
        >
          {d.seals.slice(0, 10).map((s) => (
            <li key={s.id} data-testid={`examiner-score-seal-${s.id}`}>
              <span className="tabular-nums">
                {formatDateTime(s.sealed_at, i18n.language)}
              </span>
              {" \u00b7 "}
              <span className="font-semibold">
                {s.score} {s.tier}
              </span>
              {" \u00b7 "}
              {why(s)}
              {" \u00b7 "}
              <span className="text-slate-500">{by(s)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AskTheBank() {
  const { t, i18n } = useTranslation();
  const toast = useOptionalToast();
  const rfis = useExaminer<Rfi[]>("rfis");
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    setSending(true);
    setError(null);
    try {
      const sent = await api.post<
        Rfi & { recipient_count?: number | null; recipient_names?: string[] | null }
      >("/v1/examiner/rfis", { question: question.trim() });
      setQuestion("");
      // Round 16 H30 -- the question went in and said nothing back.
      // Round 18 S4 -- and it named a seat and a count, not who answers.
      const names = sent?.recipient_names ?? [];
      toast?.push({
        tone: "success",
        message:
          names.length > 0
            ? t("examiner.rfi.sentNamed", {
                count: names.length,
                names: listOf(names),
              })
            : t("examiner.rfi.sent", { count: sent?.recipient_count ?? 0 }),
      });
      await rfis.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="space-y-2" data-testid="examiner-rfi">
      <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
        {t("examiner.rfi.heading")}
      </h2>
      <p className="max-w-3xl text-sm text-slate-500">{t("examiner.rfi.body")}</p>
      <textarea
        dir="auto"
        data-testid="examiner-rfi-question"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        rows={2}
        /* Round 12 H3 -- the one unnamed control on the examiner's own
           route, on the field that asks the bank a question. A
           placeholder is a hint, not a name, and it goes away the
           moment there is a word in the box. */
        aria-label={t("examiner.rfi.label")}
        placeholder={t("examiner.rfi.placeholder")}
        className="w-full max-w-3xl rounded border border-slate-300 px-2 py-1.5 text-sm"
      />
      <div>
        <button
          type="button"
          data-testid="examiner-rfi-ask"
          disabled={!question.trim() || sending}
          onClick={() => void ask()}
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {sending ? t("examiner.rfi.sending") : t("examiner.rfi.ask")}
        </button>
      </div>
      {error && (
        <p data-testid="examiner-rfi-error" className="text-sm text-rag-red">
          {error}
        </p>
      )}
      {rfis.data && rfis.data.length > 0 && (
        <ul data-testid="examiner-rfi-list" className="space-y-1">
          {rfis.data.map((r) => (
            <li
              key={r.id}
              data-testid={`examiner-rfi-row-${r.id}`}
              className="rounded border border-paper-2 bg-white px-3 py-2 text-sm"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span
                  className={
                    r.status === "answered"
                      ? "rounded bg-emerald-100 px-1.5 text-xs font-semibold text-emerald-800"
                      : "rounded bg-amber-100 px-1.5 text-xs font-semibold text-amber-900"
                  }
                >
                  {r.status === "answered"
                    ? t("examiner.rfi.answered")
                    : t("examiner.rfi.open")}
                </span>
                {/* Round 16 H30 -- and with whom it waits. */}
                {r.status !== "answered" && (
                  <span
                    data-testid={`examiner-rfi-waits-${r.id}`}
                    className="text-xs text-slate-500"
                  >
                    {" "}
                    {/* Round 19 S8 -- the people, as the toast named them. */}
                    {(r.recipient_names ?? []).length > 0
                      ? t("examiner.rfi.waitsWithNamed", {
                          count: (r.recipient_names ?? []).length,
                          names: listOf(r.recipient_names ?? []),
                        })
                      : t("examiner.rfi.waitsWith")}
                  </span>
                )}
                {/* ROUND 15 A15 -- separators in the TEXT, not only in
                    the gap: "13:51Answered" is what a copy and a screen
                    reader got from one row's time meeting the next
                    row's pill. */}{" "}
                <span className="text-slate-800">{r.question}</span>
                {" \u00b7 "}
                <span className="text-xs text-slate-400">
                  {formatDateTime(r.asked_at, i18n.language)}
                </span>
              </div>
              {r.answered_at && (
                <p className="mt-1 text-xs text-slate-500">
                  {t("examiner.rfi.answeredBy", {
                    who: r.answered_by ?? t("examiner.rfi.graderUnknown"),
                    at: formatDateTime(r.answered_at, i18n.language),
                  })}
                </p>
              )}
              {r.answer_note && (
                <p className="mt-1 text-xs text-slate-700">{r.answer_note}</p>
              )}
              {r.citations.length > 0 && (
                <ul
                  data-testid={`examiner-rfi-citations-${r.id}`}
                  className="mt-1 space-y-0.5 border-s-2 border-paper-2 ps-2"
                >
                  {r.citations.map((c) => (
                    <CitationLine key={`${c.kind}:${c.ref}`} c={c} />
                  ))}
                </ul>
              )}
              {r.has_pack && (
                <button
                  type="button"
                  data-testid={`examiner-rfi-pack-${r.id}`}
                  onClick={() =>
                    void downloadRfiPack(r.id).catch((e: unknown) =>
                      setError(refusalMessage(t, e)),
                    )
                  }
                  className="mt-1 text-xs font-medium text-brand-brassDark hover:underline"
                >
                  {t("examiner.rfi.downloadPack")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Names in a coherence group that carry no address.
 *
 * Round 10 F4 -- the header counts `names` and the list renders
 * `records`; a flag with no entity id contributes to the first and not
 * the second, so rendering records alone would leave the two
 * disagreeing again in exactly the way this row has disagreed for two
 * rounds.
 */
function unaddressed(g: CoherenceGroup): string[] {
  const addressed = new Set(g.records.map((r) => r.name));
  return g.names.filter((n) => !addressed.has(n));
}

interface ExaminerControlHit {
  id: string;
  framework_code: string;
  natural_id: string;
  text: string;
}

interface ExaminerControlRecord {
  id: string;
  framework_code: string;
  natural_id: string;
  domain: string;
  subdomain: string;
  text: string;
  assessment: {
    cycle_name: string;
    cycle_status: string;
    status: string;
    current_ml: number | null;
    target_ml: number;
    graded_by: string | null;
    graded_at: string | null;
    seal_slug: string | null;
    seal_entity_id: string;
    seal_rows: number;
  } | null;
  evidence: { filename: string; uri: string | null }[];
  policies: {
    document_id: string;
    filename: string;
    title: string | null;
    sha256: string | null;
    directed_passages?: number | null;
    hidden_passages?: number | null;
  }[];
}

/**
 * ROUND 15 H8, fifteenth round -- "control" and "policy" occurred zero
 * times on this page, so a supervisor lodged a question for what the
 * bank already holds: the latest assessment of a control, who graded
 * it and when, the evidence attached and the policy it reads. His token
 * cannot reach the operator's drawer; the record is here instead.
 */
function ControlLookup({
  onOpenEntity,
}: {
  onOpenEntity: (kind: string, id: string, name: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const hits = useExaminer<ExaminerControlHit[]>(
    `controls?q=${encodeURIComponent(asked)}`,
    asked.trim().length >= 2,
  );
  const record = useExaminer<ExaminerControlRecord>(
    `controls/${chosen ?? ""}`,
    chosen !== null,
  );
  const r = record.data;
  return (
    <section className="space-y-2" data-testid="examiner-controls">
      <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
        {t("examiner.controls.heading")}
      </h2>
      <p className="max-w-3xl text-sm text-slate-500">{t("examiner.controls.body")}</p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setChosen(null);
          setAsked(query.trim());
        }}
      >
        <label className="text-xs text-slate-600" htmlFor="examiner-control-q">
          {t("examiner.controls.label")}
        </label>
        <input
          id="examiner-control-q"
          data-testid="examiner-control-q"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
          dir="auto"
        />
        <button
          type="submit"
          data-testid="examiner-control-find"
          className="rounded border border-brand-brass/60 px-2 py-1 text-xs text-slate-800 hover:border-brand-brass"
        >
          {t("examiner.controls.find")}
        </button>
      </form>
      {hits.data && hits.data.length === 0 && (
        <p data-testid="examiner-controls-none" className="text-xs text-slate-500">
          {t("examiner.controls.none", { query: asked })}
        </p>
      )}
      {hits.data && hits.data.length > 0 && chosen === null && (
        <ul data-testid="examiner-control-hits" className="space-y-1 text-xs">
          {hits.data.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                data-testid={`examiner-control-hit-${h.id}`}
                onClick={() => setChosen(h.id)}
                className="text-start text-brand-brassDark hover:underline"
              >
                <span className="font-mono">
                  {h.framework_code} {h.natural_id}
                </span>
              </button>{" "}
              <span className="text-slate-600" dir="auto">
                {h.text}
              </span>
            </li>
          ))}
        </ul>
      )}
      {record.isError && (
        <p className="text-xs text-rag-red">{t("examiner.controls.error")}</p>
      )}
      {r && (
        <div
          data-testid="examiner-control-record"
          className="space-y-2 rounded border border-brand-brass/40 bg-white px-3 py-2 text-sm"
        >
          <div className="flex items-baseline gap-2">
            <span className="font-mono font-medium">
              {r.framework_code} {r.natural_id}
            </span>
            <span className="text-xs text-slate-500" dir="auto">
              {r.domain} · {r.subdomain}
            </span>
            <button
              type="button"
              onClick={() => setChosen(null)}
              className="ms-auto text-xs text-slate-500 hover:text-slate-800"
            >
              {t("examiner.entity.close")}
            </button>
          </div>
          <p className="text-xs text-slate-700" dir="auto">
            {r.text}
          </p>
          <div data-testid="examiner-control-assessment" className="text-xs">
            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
              {t("examiner.controls.assessmentHeading")}
            </p>
            {r.assessment === null ? (
              <p className="text-slate-500">{t("examiner.controls.notAssessed")}</p>
            ) : (
              <p className="text-slate-800">
                {t("examiner.controls.assessment", {
                  status: statusLabel(r.assessment.status),
                  current: r.assessment.current_ml ?? "\u2014",
                  target: r.assessment.target_ml,
                  cycle: r.assessment.cycle_name,
                })}{" "}
                {r.assessment.graded_by && r.assessment.graded_at
                  ? t("examiner.controls.gradedBy", {
                      who: r.assessment.graded_by,
                      when: formatDateTime(r.assessment.graded_at, i18n.language),
                      count: r.assessment.seal_rows,
                    })
                  : t("examiner.controls.neverSealed")}
              </p>
            )}
          </div>
          <div className="text-xs">
            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
              {t("examiner.controls.evidenceHeading")}
            </p>
            {r.evidence.length === 0 ? (
              <p className="text-slate-500">{t("examiner.controls.noEvidence")}</p>
            ) : (
              <ul className="list-disc ps-4" data-testid="examiner-control-evidence">
                {r.evidence.map((e, i) => (
                  <li key={`${e.filename}-${i}`} dir="auto">
                    {e.filename}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="text-xs">
            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
              {t("examiner.controls.policyHeading")}
            </p>
            {r.policies.length === 0 ? (
              <p className="text-slate-500">{t("examiner.controls.noPolicy")}</p>
            ) : (
              <ul className="space-y-0.5" data-testid="examiner-control-policies">
                {r.policies.map((p) => (
                  <li key={p.document_id}>
                    <button
                      type="button"
                      data-testid={`examiner-control-policy-${p.document_id}`}
                      onClick={() =>
                        onOpenEntity("document", p.document_id, p.title ?? p.filename)
                      }
                      className="text-brand-brassDark hover:underline"
                      dir="auto"
                    >
                      {p.title ?? p.filename}
                    </button>
                    <DocumentReadingNote reading={p} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export { modelsDifferSentence };

const HEX_FULL = /^[0-9a-f]{16,}$/i;

/** Round 17 A32 -- a fingerprint shortened to eight and eight cannot
 * be joined to the replay proxy's log; this copies the whole value. */
function CopyFull({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid="examiner-copy-full"
      onClick={() => {
        // Clipboard access can be refused; claim nothing that did not happen.
        void navigator.clipboard
          ?.writeText(value)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
      className="ms-1 rounded border border-slate-300 bg-white px-1 text-[10px] text-slate-600 hover:border-brand-brass"
    >
      {copied ? t("examiner.chain.copiedFull") : t("examiner.chain.copyFull")}
    </button>
  );
}

export function ExaminerWorkspace() {
  const { t, i18n } = useTranslation();
  /* Round 18 S3 -- the workspace stands in for every address an examiner
     types, so /board, /readiness or /admin/mock-exam rendered it with no
     word that the page asked for is the bank's (six logged misses). */
  const { pathname } = useLocation();
  const strayed = pathname !== "/";
  // Round 17 H38 -- does this deployment publish a signing key? Read
  // without the session's token: the page is public, and the examiner's
  // token is confined to its own routes.
  const publicKeys = useQuery({
    queryKey: ["proof", "public-keys"],
    queryFn: async () => {
      const r = await fetch("/v1/proof/public-keys");
      if (!r.ok) return { keys: [] as unknown[] };
      return (await r.json()) as { keys?: unknown[] };
    },
    staleTime: 600_000,
    retry: false,
  });
  const publishesKey = (publicKeys.data?.keys ?? []).length > 0;
  const { logout } = useAuth();
  const overview = useExaminer<ExaminerOverview>("overview");
  /* Round 16 H30 -- before a grant every read below was refused as
     well, eight 403s for one page. The overview's refusal is the one
     that says why; the rest wait until it has been admitted. */
  const admitted = overview.isSuccess;
  const checks = useExaminer<ExaminerChecks>("supervisory-checks", admitted);
  const chains = useExaminer<ExaminerChainVerify>("chain-verify", admitted);
  // CX6 -- one card at a time. An examiner opening a second record has
  // finished with the first, and two open cards is a list he did not
  // ask for.
  const [entity, setEntity] = useState<{
    kind: string;
    id: string;
    name: string;
  } | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const o = overview.data;
  return (
    <PreferencesUnreachable.Provider value={true}>
      <div
        data-testid="examiner-workspace"
        className="min-h-screen bg-paper text-slate-900"
      >
        <header className="border-b border-brand-navyLine bg-brand-navy text-white">
          <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-3">
            <span className="font-display text-lg tracking-wide">
              {t("screens.examinerWorkspace.marsad")}
            </span>
            <span className="font-arabic text-brand-brass">{t("app.titleArabic")}</span>{" "}
            <span className="ms-2 font-mono text-[11px] uppercase tracking-widest text-brand-brass">
              {t("examiner.kicker")}
            </span>
            {/* G3: the examiner shell had no language control at all, so
              an Arabic-reading examiner was stuck in English even once
              a catalogue exists. It renders nothing while the Arabic
              catalogue is a scaffold (G4), and appears here with the
              rest of them the day one lands. */}
            <span className="ms-auto flex items-center gap-3">
              <LanguageToggle />
              <button
                type="button"
                onClick={() => void logout()}
                className="text-sm text-white/80 hover:underline"
              >
                {t("auth.logout")}
              </button>
            </span>
          </div>
        </header>

        <main className="mx-auto max-w-5xl space-y-8 px-6 py-8">
          {strayed && (
            <p
              data-testid="examiner-not-yours"
              data-path={pathname}
              className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {t("examiner.notYours", { path: pathname })}{" "}
              <Link to="/" className="underline" data-testid="examiner-not-yours-home">
                {t("examiner.notYoursHome")}
              </Link>
            </p>
          )}
          <section className="space-y-1">
            <h1 className="font-display text-2xl text-slate-900">
              {t("examiner.heading")}
            </h1>
            <p className="max-w-3xl text-sm text-slate-500">{t("examiner.body")}</p>
            {/* D3 -- every number on this page was undated. An examiner
              citing a figure in a report needs to say WHEN it was
              read; without that the page drifts under him between
              opening it and writing it down. */}
            {o?.generated_at && (
              <p data-testid="examiner-as-of" className="text-xs text-slate-500">
                {t("examiner.asOf", {
                  when: formatDateTime(o.generated_at, i18n.language),
                })}
              </p>
            )}
            {o?.grant && (
              <p data-testid="examiner-grant-line" className="text-xs text-slate-500">
                {/* Round 19 S8 -- whose grant it is, then its note. */}
                {t("examiner.grantLine", {
                  username: o.grant.examiner_username,
                  scope: o.grant.scope_note,
                  date: o.grant.expires_at.slice(0, 10),
                })}
              </p>
            )}
          </section>

          {overview.isError ? (
            // B7: it said only "Could not load the examination overview",
            // which leaves a visitor unable to tell whether he was
            // revoked, whether it simply ran out, or whom to contact. The
            // server now says which; show what it said.
            <p
              data-testid="examiner-error"
              className="max-w-3xl rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              {/* Round 18 I18 -- from the catalogue, in the reader's
                  language; the server's English is the fallback. */}
              {overview.error ? refusalMessage(t, overview.error) : t("examiner.error")}
            </p>
          ) : (
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard
                testid="examiner-score"
                label={t("examiner.cards.score")}
                value={
                  !o
                    ? "…"
                    : o.score.not_scored_reason
                      ? /* Round 17 B10 -- no figure over nothing admitted. */
                        t("examiner.cards.scoreNotScored")
                      : `${o.score.score ?? "—"} · ${o.score.tier ?? "—"}`
                }
                termKey="resilience_score"
                sub={
                  o
                    ? t("examiner.cards.confidence", {
                        pct: o.score.confidence,
                      })
                    : undefined
                }
              />
              <StatCard
                testid="examiner-baseline"
                label={t("examiner.cards.baseline")}
                value={o ? `${o.baseline.baseline_pct}%` : "…"}
                sub={o?.baseline.verdict}
              />
              <StatCard
                testid="examiner-checks"
                label={t("examiner.cards.checks")}
                value={
                  o
                    ? o.checks_summary.distribution_total != null
                      ? /* Round 19 P18 -- "13 / 13" read as every judged
                           check flagged; the parts say what the 13 are. */
                        `${o.checks_summary.checks_flagged}`
                      : `${o.checks_summary.checks_flagged} / ${
                          o.checks_summary.checks_total -
                          (o.checks_summary.checks_not_assessed ?? 0)
                        }`
                    : "…"
                }
                /* Round 16 B9 -- a check with nothing to judge is out of
                 the "of", and said beside it rather than counted in. */
                sub={
                  /* Round 18 P11 -- the flagged figure counts coherence
                     flags too, which the field exam lists apart; say
                     how many, so the two pages reconcile. */
                  o?.checks_summary.distribution_total != null
                    ? [
                        t("examiner.cards.checksParts", {
                          distribution: o.checks_summary.distribution_flagged ?? 0,
                          distributionTotal: o.checks_summary.distribution_total,
                          coherence: o.checks_summary.coherence_flags ?? 0,
                        }),
                        (o.checks_summary.checks_not_assessed ?? 0) > 0
                          ? t("examiner.cards.checksSubNotAssessedPart", {
                              count: o.checks_summary.checks_not_assessed ?? 0,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(t("examiner.cards.checksSubJoin"))
                    : [
                        t("examiner.cards.checksSub"),
                        o?.checks_summary.coherence_flags != null
                          ? t("examiner.cards.checksSubCoherence", {
                              count: o.checks_summary.coherence_flags,
                            })
                          : null,
                        (o?.checks_summary.checks_not_assessed ?? 0) > 0
                          ? t("examiner.cards.checksSubNotAssessedPart", {
                              count: o?.checks_summary.checks_not_assessed ?? 0,
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(t("examiner.cards.checksSubJoin"))
                }
              />
              <StatCard
                testid="examiner-chains"
                label={t("examiner.cards.chains")}
                value={
                  chains.data
                    ? chainsTileValue(chains.data.chains, chains.data.total_breaks, t)
                    : "…"
                }
                sub={
                  chains.data
                    ? t("examiner.cards.chainsSub", {
                        rows: chains.data.total_examined,
                      })
                    : undefined
                }
              />
            </section>
          )}

          {admitted && o && (
            <ScoreToCite live={o.score.score} readAt={o.generated_at} />
          )}

          {/* ROUND 13 F8 -- criterion 12. The sovereignty claim, where a
            supervisor reads rather than in a runbook in the
            repository. Rendered whether or not it is stated: "not
            stated" is the answer to a question he came to ask, and
            silence reads as an oversight. */}
          <section
            data-testid="examiner-deployment"
            data-stated={o?.deployment?.region ? "true" : "false"}
            className="rounded border border-slate-200 bg-white p-3"
          >
            <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
              {t("examiner.sections.deployment")}
            </h2>
            {o?.deployment?.region ? (
              <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <DeploymentFact
                  testid="examiner-deployment-region"
                  label={t("examiner.deployment.region")}
                  value={o.deployment.region}
                />
                <DeploymentFact
                  testid="examiner-deployment-hosting"
                  label={t("examiner.deployment.hosting")}
                  value={o.deployment.hosting}
                />
                <DeploymentFact
                  testid="examiner-deployment-backup"
                  label={t("examiner.deployment.backup")}
                  value={o.deployment.backup}
                />
                <DeploymentFact
                  testid="examiner-deployment-restore"
                  label={t("examiner.deployment.restore")}
                  value={o.deployment.restore_tested}
                />
              </dl>
            ) : overview.isError ? (
              // ROUND 15 K9 -- criterion 12. Before the grant the overview
              // was REFUSED (403) and this branch rendered the absence as
              // "not stated": the same seat read "Saudi Arabia Central"
              // three minutes later. A refusal is not an unstated fact.
              <p
                data-testid="examiner-deployment-refused"
                className="mt-2 max-w-3xl text-sm text-slate-600"
              >
                {overview.error?.status === 403
                  ? t("examiner.deployment.refused")
                  : t("examiner.deployment.unavailable")}
              </p>
            ) : !o ? (
              <p
                data-testid="examiner-deployment-loading"
                className="mt-2 max-w-3xl text-sm text-slate-500"
              >
                …
              </p>
            ) : (
              <p
                data-testid="examiner-deployment-unstated"
                className="mt-2 max-w-3xl text-sm text-slate-600"
              >
                {t("examiner.deployment.unstated")}
              </p>
            )}
            {/* ROUND 15 K6 -- the product's own record of the last restore,
              beside the operator's typed line, and a flag when the two
              disagree. The typed line went stale the day a restore ran
              and nothing could say so. */}
            {o?.deployment?.restore_recorded_at && (
              <p
                data-testid="examiner-deployment-restore-recorded"
                className="mt-2 max-w-3xl text-sm text-slate-700"
              >
                {t("examiner.deployment.restoreRecorded", {
                  date: o.deployment.restore_recorded_at.slice(0, 10),
                })}
              </p>
            )}
            {o?.deployment?.restore_disagrees && (
              <p
                data-testid="examiner-deployment-restore-disagree"
                className="mt-1 max-w-3xl rounded border border-amber-200 bg-amber-50 px-2 py-1 text-sm text-amber-900"
              >
                {t("examiner.deployment.restoreDisagrees")}
              </p>
            )}
            <p className="mt-2 max-w-3xl text-xs text-slate-500">
              {t("examiner.deployment.source")}
            </p>
            {o?.deployment?.document_url && (
              <p className="mt-1 text-xs">
                <a
                  data-testid="examiner-deployment-doc"
                  href={o.deployment.document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-brassDark hover:underline"
                >
                  {t("examiner.deployment.document")}
                </a>
              </p>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
              {t("examiner.sections.checks")}
            </h2>
            {/* F2: an examiner arrives with no product knowledge at all,
              and these are the three constructs the workspace is built
              from. */}
            <Explainer termKey="supervisory_check" className="max-w-3xl" inline />
            {checks.data && (
              <ul data-testid="examiner-distribution" className="space-y-1">
                {checks.data.distribution.map((c) => (
                  <CheckRow
                    key={c.key}
                    check={c}
                    onOpenEntity={(kind, id, name) => setEntity({ kind, id, name })}
                  />
                ))}
                {/* D11 -- one row per OFFENDER, so a check that found
                  thirty services printed thirty near-identical lines
                  and each of them named one. An examiner cannot tell
                  from that whether he is reading one finding or thirty,
                  and thirty rows saying the same thing is the flood
                  P399 removed from the field exam. Grouped: the
                  finding once, then who it applies to. */}
                {groupCoherence(checks.data.coherence).map((g) => (
                  <li
                    key={g.key}
                    data-testid={`examiner-coherence-${g.key}`}
                    className="rounded border border-rose-200 bg-rose-50/60 px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="rounded bg-rose-100 px-1.5 text-xs font-semibold text-rose-800">
                        {t("examiner.flagged")}
                      </span>
                      <span className="font-medium">{checkLabel(t, g)}</span>
                      {/* C4 -- this read "1 record" directly above a
                        sentence saying "30 of 205 breach this
                        standard". Both were true and they counted
                        different things: the rows NAMED here, and the
                        estate the check ran over. A number beside a
                        contradicting number is worse than no number,
                        so this says which one it is. */}
                      <span className="text-xs text-slate-500">
                        {t("examiner.coherenceNamed", { count: g.names.length })}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-600">{checkDetail(t, g)}</p>
                    <p
                      data-testid={`examiner-coherence-names-${g.key}`}
                      className="mt-1 text-xs text-slate-700"
                    >
                      {/* CX6 -- the names were text. The flag knew exactly
                        which service it was about and the payload threw
                        the address away, so the one thing an examiner
                        wanted to do with a named record -- open it --
                        was the one thing he could not. A record without
                        an address still reads as a name. */}
                      {/* Round 10 F4 -- and a THIRD slice, reintroduced
                        by the change that made these names openable.
                        The row headed "12 named below", printed
                        EIGHT, and closed "-- naming 12 of 30". Round 9
                        removed the second slice from the names branch
                        and this branch grew its own. The server caps
                        at twelve, which is a list a person reads; the
                        page prints what it was sent. */}
                      {g.records.length > 0
                        ? g.records.map((r, i) => (
                            <span key={`${r.id}-${i}`}>
                              {i > 0 ? ", " : ""}
                              <button
                                type="button"
                                data-testid={`examiner-coherence-record-${r.id}`}
                                onClick={() => setEntity(r)}
                                className="text-brand-brassDark hover:underline"
                                dir="auto"
                              >
                                {r.name}
                              </button>
                            </span>
                          ))
                        : g.names.join(", ")}
                      {/* A flag with no address still reads as a name,
                        and dropping it because it cannot be opened
                        would make the header count disagree with the
                        list all over again. */}
                      {g.records.length > 0 && unaddressed(g).length > 0
                        ? `${", "}${unaddressed(g).join(", ")}`
                        : ""}
                      {/*
                        Round 9 E4 -- ONE cap, stated once, against the
                        real total.

                        This row carried three numbers: "12 named
                        below" from the server's cap, "30 of 205 breach
                        this standard" from the detail sentence, and
                        "showing 8 of 12" from a SECOND slice applied
                        here -- with only the middle one true, and a
                        closing pointer to "the standard", a surface an
                        examiner has no route to.

                        The server already caps at twelve, which is a
                        list a person reads. Slicing it again to eight
                        and then reporting the cap as the denominator
                        is how a row ends up disagreeing with itself.
                    */}
                      {g.truncated
                        ? t("examiner.coherenceMore", {
                            shown: g.names.length,
                            count: g.member_total ?? g.names.length,
                          })
                        : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {/* CX6 -- one card, one place. A coherence row and a
              distribution class both open a record, and rendering the
              card beside whichever list it came from would put it in
              two places and let two be open at once. */}
            {entity && (
              <EntityCard
                kind={entity.kind}
                id={entity.id}
                name={entity.name}
                onClose={() => setEntity(null)}
              />
            )}
          </section>

          <section className="space-y-2">
            <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
              {t("examiner.sections.chains")}
            </h2>
            <Explainer termKey="hash_chain" className="max-w-3xl" inline />
            {chains.data && (
              <table
                data-testid="examiner-chain-table"
                className="w-full border-collapse text-sm"
              >
                <thead>
                  <tr className="text-start font-mono text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="py-1 text-start">{t("examiner.chain.slug")}</th>
                    <th className="text-end">{t("examiner.chain.rows")}</th>
                    <th className="text-end">{t("examiner.chain.valid")}</th>
                    <th className="text-end">{t("examiner.chain.breaks")}</th>
                    <th className="text-end">{t("examiner.chain.checkColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {chains.data.chains.map((c) => (
                    <ChainRow
                      key={c.slug}
                      chain={c}
                      onOpenEntity={(kind, id, name) => setEntity({ kind, id, name })}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {admitted && (
            <ControlLookup
              onOpenEntity={(kind, id, name) => setEntity({ kind, id, name })}
            />
          )}

          {admitted && <AskTheBank />}

          <section className="space-y-2">
            <h2 className="font-mono text-xs uppercase tracking-wide text-slate-500">
              {t("examiner.sections.dossier")}
            </h2>
            <p className="max-w-3xl text-sm text-slate-500">
              {t("examiner.dossierBody")}
            </p>
            {/* E5 -- the button outlived the grant: after a revoke the
              page still offered the dossier and the click failed
              silently, so the examiner could not tell whether the
              export was broken or his access was gone. If the visit
              itself is refused there is nothing here to download. */}
            {overview.isError ? (
              <p
                data-testid="examiner-dossier-unavailable"
                className="text-sm text-amber-900"
              >
                {t("examiner.dossierUnavailable")}
              </p>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="examiner-dossier-download"
                  disabled={downloading}
                  onClick={() => {
                    setDownloading(true);
                    setDownloadError(null);
                    downloadDossier()
                      .catch((e: unknown) => setDownloadError(refusalMessage(t, e)))
                      .finally(() => setDownloading(false));
                  }}
                  className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
                >
                  {downloading
                    ? t("examiner.downloadingDossier")
                    : t("examiner.downloadDossier")}
                </button>
                {downloadError && (
                  <p
                    data-testid="examiner-dossier-error"
                    className="text-sm text-rag-red"
                  >
                    {downloadError}
                  </p>
                )}
              </>
            )}
            {/* Round 16 O1 -- the group register, signed, beside the
              dossier. The examiner reached it only through the admin
              API. */}
            {!overview.isError && (
              <GroupRegisterDownload path={EXAMINER_GROUP_PACK_PATH} />
            )}
            {/* B7: how to check it, and what the answers mean -- an
              examiner offered a verifier and no command is being asked
              to take the verification on trust too. */}
            <div
              data-testid="examiner-verify-help"
              className="max-w-3xl space-y-1 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
            >
              <p>{t("examiner.verify.intro")}</p>
              <pre className="overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-slate-800">
                {t("examiner.verify.command")}
              </pre>
              <p>
                <b>{t("screens.examinerWorkspace.ok")}</b> {t("examiner.verify.ok")}
              </p>
              {/* Round 19 S8 -- what the dossier's chain anchor proves. */}
              <p data-testid="examiner-verify-against">
                {t("examiner.verify.against")}
              </p>
              <pre className="overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-slate-800">
                {t("examiner.verify.againstCommand")}
              </pre>
              {/* Round 17 H38 -- the demonstration-key warning only where
                the deployment signs with one: a deployment that publishes
                an Ed25519 key never prints it. */}
              {!publishesKey && (
                <p data-testid="examiner-verify-warn">
                  <b>{t("screens.examinerWorkspace.warn")}</b>{" "}
                  {t("examiner.verify.warn")}
                </p>
              )}
              {/* ROUND 15 K10 -- the hosted pack no longer prints the
                demonstration-key warning; what it prints is that the
                embedded key is SELF-ASSERTED until pinned to the
                issuer. Say what that means, and where the issuer's key
                is published. */}
              <p data-testid="examiner-verify-self-asserted">
                <b>{t("screens.examinerWorkspace.selfAsserted")}</b>{" "}
                {t("examiner.verify.selfAsserted")}{" "}
                <a
                  href="/v1/proof/public-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-brassDark hover:underline"
                  data-testid="examiner-verify-keys-link"
                >
                  {t("examiner.verify.keysLink")}
                </a>
                {o?.deployment?.document_url ? (
                  <>
                    {" \u00b7 "}
                    <a
                      href={o.deployment.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-brassDark hover:underline"
                    >
                      {t("examiner.deployment.document")}
                    </a>
                  </>
                ) : null}
              </p>
            </div>
          </section>
        </main>
      </div>
    </PreferencesUnreachable.Provider>
  );
}

/** Round 18 B22 -- the tile read "✓ · 17" over one row re-walked: sixteen
 *  of the seventeen chains had never been written. A tick needs a walk,
 *  and the count says how many chains had one. */
export function chainsTileValue(
  chains: { examined: number }[],
  breaks: number,
  t: TFunction,
): string {
  const written = chains.filter((c) => c.examined > 0).length;
  if (breaks > 0) return `${breaks} · ${chains.length}`;
  if (written === chains.length) return `✓ · ${chains.length}`;
  return t("examiner.cards.chainsWritten", {
    mark: written === 0 ? "--" : "✓",
    written,
    total: chains.length,
  });
}
